import React from 'react';
import { Link, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { Badge } from '@startuptools/ui/Badge';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { IconButton } from '@startuptools/ui/IconButton';
import { Sheet } from '@startuptools/ui/Sheet';
import { Tab } from '@startuptools/ui/Tab';
import { TabList } from '@startuptools/ui/TabList';
import { Tabs } from '@startuptools/ui/Tabs';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { CapTable } from './CapTable';
import { CapTableForm } from './ui/Form';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { EmptyState } from './EmptyState';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { EditCapTable } from './EditCapTable';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GenericError } from '@startuptools/ui/Error';
import { Stack } from '@startuptools/ui/Stack';
import AddIcon from '@mui/icons-material/Add';
import { CapTablesDocument, useCapTablesQuery, useCreateCapTableMutation } from './cap-table.graphql';
import { useNavigate } from '../../components/Navigate';

const CapTables = () => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const { capTableId } = useParams();
  const { data, loading, error } = useCapTablesQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });
  const [createCapTable, { loading: createLoading, error: createError }] = useCreateCapTableMutation({
    refetchQueries: [CapTablesDocument],
  });
  const [editCapTable, setEditCapTable] = React.useState<false | string>(false);

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const openCreateDialog = React.useCallback(() => setCreateDialogOpen(true), []);
  const closeCreateDialog = React.useCallback(() => setCreateDialogOpen(false), []);
  const { link, navigate } = useNavigate();

  const trialUntil = data?.company.capTableTrialExpires
    ? DateTime.fromISO(data.company.capTableTrialExpires)
    : undefined;

  const trialActive = trialUntil && trialUntil >= DateTime.now();
  const isPro = data?.companySubscription?.canUseProFeatures || data?.company.isDemo;
  const canUseCapTable = isPro || trialActive;

  const content = React.useMemo(() => {
    if (loading) {
      return null;
    }
    if (!canUseCapTable) {
      return (
        <Alert
          endDecorator={
            <Button component={Link} to={link('/settings/billing')}>
              <Trans id="Upgrade" />
            </Button>
          }
        >
          <Trans id="You have to upgrade to a Pro subscription to create CapTables." />
        </Alert>
      );
    }
    if (!data?.capTables.length) {
      return (
        <Stack alignItems="center">
          <EmptyState onCreateClick={openCreateDialog} />
        </Stack>
      );
    }
    return (
      <>
        <Tabs
          key={capTableId}
          value={capTableId}
          onChange={(_e, value) => {
            if (value === 'add-new') return;
            void navigate(`/cap-tables/${value}`, undefined, { replace: true });
          }}
        >
          <TabList>
            {data?.capTables.map(c => (
              <Tab
                key={c.id}
                value={c.id}
                onDoubleClick={() => {
                  setEditCapTable(c.id);
                }}
              >
                {c.displayName}
              </Tab>
            ))}
            <Tab value="add-new">
              <IconButton onClick={openCreateDialog}>
                <AddIcon />
              </IconButton>
            </Tab>
          </TabList>
          <Outlet />
        </Tabs>
      </>
    );
  }, [loading, canUseCapTable, data?.capTables, capTableId, openCreateDialog, navigate, link]);

  React.useEffect(() => {
    if (!capTableId && data?.capTables.length) {
      void navigate(`/cap-tables/${data.capTables[0]!.id}`, undefined, { replace: true });
    }
  }, [capTableId, data, navigate]);

  if (loading || error) {
    return null;
  }

  return (
    <Box>
      <Badge
        badgeContent={<Trans id="Pro" />}
        slotProps={{
          badge: {
            sx: theme => ({
              right: '-20px',
              backgroundColor: theme.palette.pro.dark,
              color: theme.palette.text.primary,
            }),
          },
        }}
      >
        <Typography level="h2">
          <Trans id="CapTable" />
        </Typography>
      </Badge>
      <Stack gap={2}>
        {trialActive && (
          <Alert
            sx={theme => ({ backgroundColor: theme.palette.pro.dark })}
            endDecorator={
              <Button component={Link} to={link('/settings/billing')}>
                <Trans id="Upgrade" />
              </Button>
            }
          >
            <Trans
              id="You can create & edit CapTables until {date}. Like it? Upgrade to Pro!"
              values={{
                date: (
                  <DateFormat locale={i18n.locale} inline>
                    {trialUntil}
                  </DateFormat>
                ),
              }}
            />
          </Alert>
        )}
        {data?.nextShareCapitalChanges.inSync === false && canUseCapTable && (
          <Alert
            endDecorator={
              <Button component={Link} to={link('/share-register/transactions')}>
                <Trans id="Update Share Register" />
              </Button>
            }
          >
            <Trans id="The share register is not up to date with the latest events from Bolagsverket, which can cause inaccuracies in the CapTable." />
          </Alert>
        )}
        {content}
        {createDialogOpen && (
          <CapTableForm
            mode="create"
            hasWise={(data?.warrantPrograms.totalCount ?? 0) > 0}
            onCancel={() => setCreateDialogOpen(false)}
            loading={createLoading}
            error={Boolean(createError)}
            onSubmit={async formData => {
              const res = await createCapTable({
                variables: {
                  companyId,
                  input: {
                    ...formData,
                    recordDate: DateTime.now().toUTC().toString(),
                  },
                },
              });
              if (!res.data) return;
              closeCreateDialog();
              void navigate(`/cap-tables/${res.data.createCapTable.id}`);
            }}
            defaultValues={{
              displayName: data?.capTables.length ? `CapTable ${data.capTables.length + 1}` : `CapTable`,
              fullyDiluted: true,
              recordDate: null,
            }}
          />
        )}
        {editCapTable && <EditCapTable id={editCapTable} close={() => setEditCapTable(false)} />}
      </Stack>
    </Box>
  );
};

export const CapTablesRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<CapTables />}>
        <Route
          path=":capTableId"
          element={<CapTable />}
          errorElement={
            <Sheet sx={{ p: 2 }} variant="plain">
              <GenericError />
            </Sheet>
          }
        />
      </Route>
    </Routes>
  );
};
